"use client";

import React from "react";
import { Check, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const plans = [{
  id: "starter",
  name: "Starter",
  duration: "Month",
  price: "$17",
  pricey: "$180",
  description: "Essential AI tools to launch your digital presence with speed.",
  benefits: ["1 Ai-Site Generations", "Standard Cloud Performance", "Basic SEO Optimization", "24/7 Community Support"],
  isPopular: false,
  buttonText: "Start Now",
  link: "https://account.werkys.com/checkout?product_id=598&period=monthly"
}, {
  id: "pro",
  name: "Pro",
  duration: "Month",
  price: "$29",
  pricey: "$300",
  description: "The ultimate Werkys experience with cat-like reflexes and agility.",
  benefits: ["Unlimited Site Generations", "Ultra-High Performance", "Advanced AI Brand Kits", "Priority 1-on-1 Support", "Real-time Analytics Dashboard"],
  isPopular: true,
  buttonText: "Go Pro Now",
  link: "https://account.werkys.com/checkout?product_id=600&period=monthly"
}, {
  id: "elite",
  name: "Elite",
  duration: "Month",
  price: "$54",
  pricey: "$540",
  description: "Enterprise-grade performance for high-growth organizations.",
  benefits: ["Dedicated AI Resources", "Maximum Security Suite", "E-commerce", "SLA Performance Guarantee", "White-label Deployment"],
  isPopular: false,
  buttonText: "Become Elite",
  link: "https://account.werkys.com/checkout?product_id=601&period=monthly"
}];
export default function NosForfaitsPremium() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224297" id="choose-your-path" ref={sectionRef} className="bg-background text-foreground py-24 px-4 relative overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/20 blur-[120px] rounded-full opacity-50" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[400px] bg-primary/10 blur-[100px] rounded-full opacity-30" />
      </div>

     <div className="max-w-7xl mx-auto relative z-10">
        <div className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="font-default text-5xl md:text-6xl font-bold mb-6 tracking-tight">
            Choose Your <span className="text-primary">Path</span>
          </h2>
          <p className="text-muted-foreground text-lg font-sans">Scale your vision with Werkys. Our flexible plans are designed to provide the agility and performance your business demands. Start with a 7-day trial.</p>
          <div className="mb-10 flex flex-row items-center justify-center">
            <div className="mt-10 text-center font-default text-lg font-bold">Need highly customizable plan ?</div>
            <Button asChild size="lg" className={`w-64 ml-10 mt-10 font-bold text-base transition-all duration-300 rounded-xl ${false ? "bg-white text-primary hover:bg-white/90 border-0 shadow-lg group relative overflow-hidden" : "bg-white text-primary hover:bg-white/90 border border-primary shadow-md"}`}>
                <Link to="#">
                {false && <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out" />}
                <span className="relative z-10">Contact Sales</span>
              </Link>
            </Button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 items-center max-w-6xl mx-auto">
          {plans.map((plan, i) => <div data-index={i} key={plan.id} className={`transition-all duration-700 delay-[${i * 150}ms] transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"} ${plan.isPopular ? "md:order-2" : i === 0 ? "md:order-1" : "md:order-3"}`}>
              <Card className={`relative h-full flex flex-col backdrop-blur-sm border-0 transition-all duration-300 rounded-3xl ${plan.isPopular ? "md:scale-110 z-20 bg-primary text-primary-foreground shadow-2xl" : "z-10 bg-card text-card-foreground shadow-md hover:shadow-xl"}`}>
                {plan.isPopular && <div className="absolute -top-4 left-0 right-0 flex justify-center z-30">
                    <Badge className="bg-white text-primary px-4 py-1 text-sm font-bold uppercase tracking-wider shadow-lg flex items-center gap-1 rounded-full">
                      <Sparkles className="w-4 h-4" />
                      Most Popular
                    </Badge>
                  </div>}

                <CardHeader className="text-center pb-2 pt-8">
                  <CardTitle className={`font-default text-2xl mb-2 ${plan.isPopular ? "text-white" : "text-primary"}`}>
                    {plan.name}
                  </CardTitle>
                  <div className="flex justify-center items-baseline gap-1 mb-2">
                    <span className={`font-default text-5xl  ${plan.isPopular ? "text-white" : "text-foreground"}`}>
                      {plan.price}
                    </span>
                    <span className={`text-lg font-medium ${plan.isPopular ? "text-white/80" : "text-muted-foreground"}`}>
                      / {plan.duration}
                    </span>
                  </div>
                  <div className="flex justify-center items-baseline gap-1 mb-2">
                    <span className={`font-default text-5xl font-bold ${plan.isPopular ? "text-white" : "text-foreground"}`}>
                      {plan.pricey}
                    </span>
                    <span className={`text-lg font-medium ${plan.isPopular ? "text-white/80" : "text-muted-foreground"}`}>
                      / Yearly
                    </span>
                  </div>
                  <CardDescription className={`text-sm font-sans min-h-[40px] ${plan.isPopular ? "text-white/90" : "text-muted-foreground"}`}>
                    {plan.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="flex-grow pt-6">
                  <ul className="space-y-4">
                    {plan.benefits.map((benefit, index) => <li key={index} data-index={index} className="flex items-start gap-3 text-sm font-sans">
                        <div className={`mt-0.5 p-1 rounded-full shrink-0 ${plan.isPopular ? "bg-white/20" : "bg-primary/10"}`}>
                          <Check className={`w-4 h-4 ${plan.isPopular ? "text-white" : "text-primary"}`} />
                        </div>
                        <span className={plan.isPopular ? "text-white" : "text-foreground/90"}>{benefit}</span>
                      </li>)}
                  </ul>
                </CardContent>

                <CardFooter className="pt-6 pb-8">
                  <Button asChild size="lg" className={`w-full font-bold text-base transition-all duration-300 rounded-xl ${plan.isPopular ? "bg-white text-primary hover:bg-white/90 border-0 shadow-lg group relative overflow-hidden" : "bg-white text-primary hover:bg-white/90 border border-primary shadow-md"}`}>
                      <Link to="#" onClick={() => window.location.href = plan.link}>
                      {plan.isPopular && <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out" />}
                      <span className="relative z-10">{plan.buttonText}</span>
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    <div className="flex flex-col items-center justify-center">
          <div className="mt-20 text-center font-default text-5xl font-bold">Not sure yet ?</div>
          <Button asChild size="lg" className={`w-64 mt-20 font-bold text-base transition-all duration-300 rounded-xl ${false ? "bg-white text-primary hover:bg-white/90 border-0 shadow-lg group relative overflow-hidden" : "bg-white text-primary hover:bg-white/90 border border-primary shadow-md"}`}>
              <Link to="#" onClick={() => window.location.href = "https://account.werkys.com/sign-up"}>
              {false && <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out" />}
              <span className="relative z-10">Start Trial</span>
            </Link>
          </Button>
        </div>
    </section>;
}